import { forwardRef, memo, type MouseEventHandler, type ReactNode, type ElementType } from 'react';
import isNull from 'lodash/isNull';
import isBoolean from 'lodash/isBoolean';
import startsWith from 'lodash/startsWith';
import clsx from 'clsx';
import { Link as RouterLink } from 'react-router-dom';
import { useIntl } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import { type BoxProps, type SystemProps } from '@mui/system/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Grid, { type GridSize } from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
// Material Icon imports
import DragIndicator from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
// SCSS imports
import {
  item, card, roleCard, bold, itemFull, itemRelative, smallCard, removeContainer, removeContainerSm, removeBtn, removeBtnSm,
  draggableCard, draggableCursor, dragging, selectBorder, selectBorderActive, shadyFooter, activeCard
} from './ItemCard.module.scss';

const stretchWithStatusSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column' } as const;
const stretchSx: SxProps<Theme> = { height: '100%', ...stretchWithStatusSx } as const;
const actionAreaStretchSx: SxProps<Theme> = {
  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch'
} as const;

type ItemCardProps = Omit<BoxProps, 'children' | 'className' | 'onClick'> &
  Omit<SystemProps, 'children' | 'className' | 'onClick'> & {
  readonly children: ReactNode;
  readonly badge?: boolean;
  readonly small?: boolean;
  readonly smallItem?: boolean; // together with `header` and `footer` is used for Manager variant of OpportunityCard
  readonly active?: boolean;
  readonly light?: boolean;
  readonly flat?: boolean;
  readonly selected?: boolean;
  readonly onSelect?: MouseEventHandler<HTMLButtonElement>;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  readonly onRemoveClick?: MouseEventHandler<HTMLButtonElement>;
  readonly removeAriaLabel?: string;
  readonly RemoveIcon?: ElementType;
  readonly link?: string;
  readonly href?: string;
  readonly bottomPadding?: boolean;
  readonly className?: string;
  readonly cardClassName?: string;
  readonly fullWidth?: boolean;
  readonly header?: ReactNode;
  readonly footer?: ReactNode;
  readonly status?: ReactNode;
  readonly disabled?: boolean | null;
  readonly draggable?: boolean;
  readonly isDragging?: boolean;
  readonly sm?: GridSize | null;
  readonly md?: GridSize | null;
  readonly lg?: GridSize | null;
  readonly xl?: GridSize | null;
};

const ItemCard = forwardRef<HTMLDivElement, ItemCardProps>(({
  children,
  badge = false,
  small = false,
  smallItem = false,
  active = false,
  light = false,
  flat = false,
  selected,
  onSelect,
  onClick,
  onRemoveClick,
  removeAriaLabel,
  RemoveIcon,
  link,
  href,
  bottomPadding = false,
  className,
  cardClassName,
  fullWidth = false,
  header,
  footer,
  status,
  disabled = false,
  draggable = false,
  isDragging = false,
  sm,
  md,
  lg,
  xl,
  ...props
  // eslint-disable-next-line complexity
}, ref) => {
  const { formatMessage } = useIntl();

  const content = (
    <>
      {header ? (
        <Box
            px={smallItem ? 1.5 : 2.5}
            pt={smallItem ? 1.5 : 1.25}
            pb={smallItem ? 1 : 1.25}
            bgcolor={selected ? 'primary.main' : undefined}
            display="flex"
        >
          {header}
        </Box>
      ) : undefined}
      <Box
          flex={small || fullWidth ? undefined : 1}
          pl={(header && (smallItem ? 1.5 : 2.5)) || ((small || smallItem) && 3.5) || 1.5}
          pr={(header && (smallItem ? 1.5 : 2.5)) || ((small || smallItem) && 4.5) || (light && 1) || (badge ? 0.5 : 1.5)}
          pt={(header && 0.5) || ((small || smallItem) && 2) || (light || badge ? 1.5 : 3)}
          pb={(header && smallItem && 1) || ((small || smallItem) && 2) || (bottomPadding ? 3 : 1.5)}
          color={(!isDragging && (
            (active && 'primary.contrastText') ||
            (light && 'text.label') ||
            'secondary.text'
          )) || undefined}
          bgcolor={(!isDragging && !onSelect && !isBoolean(selected) && (
            (active && 'primary.main') ||
            (light && 'background.paper') ||
            'background.card'
          )) || undefined}
          className={clsx(light ? roleCard : card, className, { [bold]: active })}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
      >
        {children}
      </Box>
      {footer ? (
        <Box
            pl={smallItem ? 1.5 : 1.25}
            pr={smallItem ? 1.5 : 0.25}
            py={1}
            bgcolor={isDragging ? undefined : 'background.paper'}
            display="flex"
            flexDirection="column"
            className={smallItem ? shadyFooter : undefined}
        >
          {footer}
        </Box>
      ) : undefined}
    </>
  );

  const RemoveButtonIcon = RemoveIcon || CloseIcon;

  const cardContent = (
    <Card
        ref={small || draggable ? ref : undefined}
        elevation={flat || onSelect || isBoolean(selected) ? 0 : 9}
        className={clsx(cardClassName, {
          [activeCard]: active,
          [smallCard]: small,
          [draggableCard]: draggable,
          [draggableCursor]: draggable && !disabled,
          [dragging]: isDragging,
          [selectBorder]: selected === false || (Boolean(onSelect) && !selected),
          [selectBorderActive]: selected === true
        })}
        sx={small || fullWidth ? undefined : (status && stretchWithStatusSx) || stretchSx}
    >
      {!isDragging && onRemoveClick ? (
        <Box className={onSelect || header ? removeContainerSm : removeContainer}>
          <Button
              color="inherit"
              variant="outlined"
              aria-label={removeAriaLabel || formatMessage({ id: 'common.button.remove' })}
              onClick={onRemoveClick}
              className={onSelect || header ? removeBtnSm : removeBtn}
              disabled={disabled ? true : undefined}
          >
            <RemoveButtonIcon fontSize={onSelect || header ? 'small' : undefined}/>
          </Button>
        </Box>
      ) : undefined}
      {(draggable && (
        <Box display="flex" flex={1}>
          <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              color={(disabled && 'action.disabled') || (isDragging ? undefined : 'secondary.main')}
              bgcolor={isDragging ? undefined : 'background.dragIcon'}
          >
            <DragIndicator/>
          </Box>
          <Box flex="1 1 0">
            {content}
          </Box>
        </Box>
      )) ||
      ((onSelect || onClick) && (
        <CardActionArea
            onClick={onSelect || onClick}
            disabled={disabled ? true : undefined}
            sx={small || fullWidth ? undefined : actionAreaStretchSx}
        >
          {content}
        </CardActionArea>
      )) ||
      (link && (
        <CardActionArea
            component={RouterLink}
            to={link}
            sx={small || fullWidth ? undefined : actionAreaStretchSx}
        >
          {content}
        </CardActionArea>
      )) || (href && (
        <CardActionArea
            component="a"
            href={href}
            target={startsWith(href, 'mailto:') ? undefined : '_blank'}
            rel="noopener noreferrer"
            sx={small || fullWidth ? undefined : actionAreaStretchSx}
        >
          {content}
        </CardActionArea>
      )) || content}
    </Card>
  );

  return small ? (
    <>
      {cardContent}
      {status}
    </>
  ) : (
    <Grid
        ref={draggable ? undefined : ref}
        size={{
          xs: 12,
          sm: fullWidth || isNull(sm) ? undefined : sm || 6,
          md: fullWidth || isNull(md) ? undefined : md || 4,
          lg: fullWidth || isNull(lg) ? undefined : lg || 3,
          xl: fullWidth || !xl ? undefined : xl
        }}
        className={clsx({
          [itemFull]: fullWidth,
          [item]: !fullWidth,
          [itemRelative]: onRemoveClick
        })}
    >
      {cardContent}
      {status}
    </Grid>
  );
});

ItemCard.displayName = 'ItemCard';

export default memo(ItemCard);
